import { useCallback, useEffect, useRef } from 'react';
import { t, Trans } from '@lingui/macro';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Grid,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTypedHash, useTypedParams } from 'react-router-typesafe-routes/dom';
import { ArrowLeft } from 'tabler-icons-react';

import { TicketQr } from '../../components/ticket-qr/ticket-qr';
import { TimeBlock } from '../../components/train-route-info/train-route-info';
import { UiCard } from '../../components/ui/ui-card/ui-card';
import { UiTicket } from '../../components/ui/ui-ticket/ui-ticket';
import { useOrders } from '../../domains/cabinet/features/orders/orders.provider';
import { RefundTicketSection } from '../../domains/cabinet/features/tickets/refund-ticket-button';
import { checkRefundForPayedTicket } from '../../domains/cabinet/features/tickets/use-ticket-refund';
import { AddFeedback } from '../../domains/feedback/add-feedback';
import { DisplayFeedback } from '../../domains/feedback/display-feedback';
import { checkMoreThreeDaysAfterTrip } from '../../domains/feedback/feedback-orders.store';
import { EOrderStatus, TOrder } from '../../domains/orders/core/order';
import { UserTimeZoneNotify } from '../../domains/tz-notify/tz-notify';
import { MainSideMenu } from '../../features/main-side-menu/main-side-menu';
import { getNameLocal } from '../../lib/local/get-title';
import { useLocal } from '../../lib/local/use-local';
import { routeCabinet } from '../../router/routes';
import { TicketDescriptionList } from './ticket-description-list/ticket-description-list';
import { TicketPassengers } from './ticket-pass';
import { TicketTariffs } from './ticket-tariffs/ticket-tariffs';
import { TripData } from './trip-data/trip-data';

import styles from './ticket-details.module.css';

export const TicketDetailsPage = observer(() => {
  const { getOrdersRequest, ordersStore } = useOrders();
  const [lang] = useLocal();
  const navigate = useNavigate();
  const { id } = useTypedParams(routeCabinet.TICKET);
  const isLoading = getOrdersRequest.isWaitingForLoad;

  const ticket = ordersStore.getMainTicketByOrderId(id || 0);
  const order = ordersStore.getOrderById(id || 0);
  const passengers = ordersStore.getPassengersByOrderId(id || 0);
  const tariffs = ordersStore.getTariffsByOrderId(id || 0);
  const trip = ordersStore.getTripById(order?.tripId || 0);

  const isTicketWaiting = ticket?.status === EOrderStatus.new;
  const isTicketDone = ticket?.status === EOrderStatus.success;
  const isTicketBooked = ticket?.status === EOrderStatus.booked;
  const isTicketError = ticket?.status === EOrderStatus.error;
  const isTicketCanceled = ticket?.status === EOrderStatus.canceled;

  const isTripDone =
    new Date(ticket?.arrivalTime || Date.now() + 1000) < new Date();

  const isMoreThreeDaysAfterTrip = checkMoreThreeDaysAfterTrip(
    ticket?.arrivalTime || ''
  );

  const isFeedbackRequired =
    order?.feedback === null && isTripDone && !isMoreThreeDaysAfterTrip;

  const isFeedbackVisible = order?.feedback !== null && !isFeedbackRequired;

  const descriptionList = [
    {
      label: t`Номер заказа`,
      value: ticket?.orderId ? ` ${ticket?.orderId}` : '',
    },
    {
      label: t`Дата заказа`,
      value: ticket?.date ? new Date(ticket?.date || 0).toLocaleString() : '',
    },
  ];

  const handleOrderUpdate = useCallback(
    (order: TOrder) => {
      ordersStore.updateOrder(order);
    },
    [ordersStore]
  );

  const hasHash = useTypedHash(routeCabinet.TICKET);
  const scrollElRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasHash && scrollElRef.current) {
      scrollElRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hasHash]);

  if (isLoading)
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.backButton}>
            <ActionIcon
              variant="transparent"
              radius="50%"
              size="40px"
              bg="transparent"
              onClick={() => navigate(routeCabinet.path)}
            >
              <ArrowLeft size={24} strokeWidth={2} color="white" />
            </ActionIcon>
          </div>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные билета</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>

        <main className={styles.main}>
          <UiCard>
            <Box pos="relative" mih={150}>
              <LoadingOverlay visible />
            </Box>
          </UiCard>
        </main>
      </div>
    );

  if (!id)
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные билета</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>
        <main className={styles.main}>
          <Stack spacing="sm">
            <UiCard>
              <Trans>
                Отсутствует ID платежа. Купленные билеты можно посмотреть в
                разделе "Мои билеты"
              </Trans>
            </UiCard>
            <UiCard>
              <Button
                size="lg"
                fullWidth
                onClick={() => navigate(routeCabinet.TICKETS.path)}
              >
                <Trans>Мои билеты</Trans>
              </Button>
            </UiCard>
          </Stack>
        </main>
      </div>
    );

  if (!isLoading && !ticket) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные заказа</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>
        <main className={styles.main}>
          <Stack spacing="sm">
            <UiCard>
              <Trans>
                Билет не найден. Попробуйте обновить страницу или вернуться к
                списку билетов
              </Trans>
            </UiCard>
            <UiCard>
              <Button
                size="lg"
                fullWidth
                onClick={() => navigate(routeCabinet.TICKETS.path)}
              >
                <Trans>Мои билеты</Trans>
              </Button>
            </UiCard>
          </Stack>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.backButton}>
          <ActionIcon
            variant="transparent"
            radius="50%"
            size="40px"
            bg="transparent"
            onClick={() => navigate(routeCabinet.path)}
          >
            <ArrowLeft size={24} strokeWidth={2} color="white" />
          </ActionIcon>
        </div>
        <div className={styles.routeTitle}>
          <h1 className={styles.headerTitle}>
            <Trans>Данные билета</Trans>
          </h1>
        </div>

        <div className={styles.mainSideMenu}>
          <MainSideMenu />
        </div>
      </header>

      <main className={styles.main}>
        <Box pos="relative">
          <Stack spacing="sm">
            <UserTimeZoneNotify />

            {isTicketDone && ticket && (
              <UiTicket>
                <Box mt={4}>
                  <TicketQr data={ticket.qr} />
                </Box>
                <Grid align="center" justify="center" gutter={10}>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, ticket.stationFrom)}
                      date={new Date(ticket.departureTime)}
                    />
                  </Grid.Col>
                  <Grid.Col span="auto">
                    <Divider my={4} />
                  </Grid.Col>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, ticket.stationTo)}
                      date={new Date(ticket.arrivalTime)}
                      direction="end"
                    />
                  </Grid.Col>
                </Grid>

                <hr
                  style={{
                    height: 1,
                    border: 0,
                    backgroundColor: '#F3F3F3',
                    margin: '12px 0',
                  }}
                />

                <TripData trip={trip} />

                <hr
                  style={{
                    height: 1,
                    border: 0,
                    backgroundColor: '#F3F3F3',
                    margin: '12px 0',
                  }}
                />

                <TicketPassengers passengers={passengers} />
              </UiTicket>
            )}

            <UiCard title={<Trans>Тарифы</Trans>}>
              <TicketTariffs tariffs={tariffs} />
            </UiCard>

            {isTicketDone && isFeedbackRequired && (
              <UiCard ref={scrollElRef} title={<Trans>Оцените поездку</Trans>}>
                <AddFeedback orderId={ticket.orderId} />
              </UiCard>
            )}

            {isTicketDone && isFeedbackVisible && (
              <UiCard ref={scrollElRef} title={<Trans>Отзыв о поездке</Trans>}>
                <DisplayFeedback feedback={order?.feedback} />
              </UiCard>
            )}

            <UiCard title={<Trans>Данные заказа</Trans>}>
              <TicketDescriptionList list={descriptionList} />

              {order &&
                checkRefundForPayedTicket(ticket, order?.refundStopDelay) && (
                  <>
                    <hr
                      style={{
                        height: 1,
                        border: 0,
                        backgroundColor: '#F3F3F3',
                        margin: '12px 0',
                      }}
                    />

                    <RefundTicketSection
                      order={order}
                      ticket={ticket}
                      trip={trip}
                      onOrderUpdate={handleOrderUpdate}
                    />
                  </>
                )}
            </UiCard>

            {isTicketBooked && (
              <UiCard>
                <Text>
                  <Trans>Заказ был забронирован и ожидает оплаты</Trans>
                </Text>
              </UiCard>
            )}

            {isTicketCanceled && (
              <UiCard>
                <Text>
                  <Trans>
                    Заказ был отменен. Стоимость оплаченного билета будет
                    возвращена с помощью выбранного способа оплаты (банковская
                    карта)
                  </Trans>
                </Text>
              </UiCard>
            )}

            {isTicketError && (
              <>
                <UiCard>
                  <Text>
                    <Trans>При обработке платежа произошла ошибка</Trans>
                  </Text>
                </UiCard>

                <UiCard>
                  <Stack spacing="sm">
                    <Button
                      size="lg"
                      fullWidth
                      onClick={() => window.location.reload()}
                    >
                      <Trans>Обновить статус</Trans>
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => navigate(routeCabinet.TICKETS.path)}
                      fullWidth
                    >
                      <Trans>Мои билеты</Trans>
                    </Button>
                  </Stack>
                </UiCard>
              </>
            )}

            {isTicketWaiting && (
              <>
                <UiCard>
                  <Text>
                    <Trans>
                      Заказ обрабатывается, ожидаем подтверждения оплаты
                    </Trans>
                    .
                    <br />
                    <Trans>
                      Попробуйте обновить статус, либо проверить купленные
                      билеты в разделе "Мои билеты"
                    </Trans>
                  </Text>
                </UiCard>

                <UiCard>
                  <Stack spacing="sm">
                    <Button
                      size="lg"
                      onClick={() => window.location.reload()}
                      fullWidth
                    >
                      <Trans>Обновить статус</Trans>
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => navigate(routeCabinet.TICKETS.path)}
                      fullWidth
                    >
                      <Trans>Мои билеты</Trans>
                    </Button>
                  </Stack>
                </UiCard>
              </>
            )}
          </Stack>
        </Box>
      </main>
    </div>
  );
});
